/**
 * SlideIndex - Manage slides in presentation
 * 
 * Maintains slide order and provides fast lookup
 */

import { Slide, SlideQuery } from '../slides/types';

export class SlideIndex {
  // Slides array (ordered)
  private slides: Slide[];
  
  // Slide lookup by ID (O(1))
  private slideMap: Map<string, Slide>;
  
  constructor() {
    this.slides = [];
    this.slideMap = new Map();
    
    console.log('[SlideIndex] Created');
  }
  
  /**
   * Add slide
   */
  addSlide(slide: Slide, index?: number): void {
    if (index !== undefined) {
      // Insert at specific index
      this.slides.splice(index, 0, slide);
      this.reindexSlides();
    } else {
      // Add at end
      slide.index = this.slides.length;
      this.slides.push(slide);
    }
    
    this.slideMap.set(slide.id, slide);
    console.log('[SlideIndex] Added slide:', slide.id, 'at index', slide.index);
  }
  
  /**
   * Remove slide
   */
  removeSlide(id: string): void {
    const slide = this.slideMap.get(id);
    if (!slide) return;
    
    this.slides.splice(slide.index, 1);
    this.slideMap.delete(id);
    this.reindexSlides();
    
    console.log('[SlideIndex] Removed slide:', id);
  }
  
  /**
   * Update slide
   */
  updateSlide(slide: Slide): void {
    const existingSlide = this.slideMap.get(slide.id);
    if (!existingSlide) return;
    
    // Update in array
    this.slides[existingSlide.index] = slide;
    
    // Update in map
    this.slideMap.set(slide.id, slide);
    
    console.log('[SlideIndex] Updated slide:', slide.id);
  }
  
  /**
   * Move slide
   */
  moveSlide(id: string, newIndex: number): void {
    const slide = this.slideMap.get(id);
    if (!slide) return;
    
    const oldIndex = slide.index;
    
    // Remove from old position
    this.slides.splice(oldIndex, 1);
    
    // Insert at new position
    this.slides.splice(newIndex, 0, slide);
    
    // Reindex all slides
    this.reindexSlides();
    
    console.log('[SlideIndex] Moved slide:', id, 'from', oldIndex, 'to', newIndex);
  }
  
  /**
   * Get slide by ID (O(1))
   */
  getSlide(id: string): Slide | null {
    return this.slideMap.get(id) || null;
  }
  
  /**
   * Get slide by index (O(1))
   */
  getSlideByIndex(index: number): Slide | null {
    return this.slides[index] || null;
  }
  
  /**
   * Query slides
   */
  query(query: SlideQuery): Slide[] {
    switch (query.type) {
      case 'byIndex':
        const slide = this.getSlideByIndex(query.index);
        return slide ? [slide] : [];
        
      case 'byId':
        const slideById = this.getSlide(query.id);
        return slideById ? [slideById] : [];
        
      case 'range':
        return this.slides.slice(query.start, query.end + 1);
        
      case 'all':
        return [...this.slides];
    }
  }
  
  /**
   * Get all slides
   */
  getAllSlides(): Slide[] {
    return [...this.slides];
  }
  
  /**
   * Get slide count
   */
  getSlideCount(): number {
    return this.slides.length;
  }
  
  /**
   * Get previous slide
   */
  getPreviousSlide(currentIndex: number): Slide | null {
    if (currentIndex <= 0) return null;
    return this.getSlideByIndex(currentIndex - 1);
  }
  
  /**
   * Get next slide
   */
  getNextSlide(currentIndex: number): Slide | null {
    if (currentIndex >= this.slides.length - 1) return null;
    return this.getSlideByIndex(currentIndex + 1);
  }
  
  /**
   * Duplicate slide
   */
  duplicateSlide(id: string): Slide | null {
    const slide = this.getSlide(id);
    if (!slide) return null;
    
    const newSlide: Slide = {
      ...slide,
      id: `slide_${Date.now()}`,
      index: slide.index + 1
    };
    
    this.addSlide(newSlide, slide.index + 1);
    
    return newSlide;
  }
  
  /**
   * Reindex all slides after changes
   */
  private reindexSlides(): void {
    this.slides.forEach((slide, index) => {
      slide.index = index;
    });
  }
  
  /**
   * Clear all slides
   */
  clear(): void {
    this.slides = [];
    this.slideMap.clear();
    console.log('[SlideIndex] Cleared');
  }
  
  /**
   * Build index from slides array
   */
  buildFromSlides(slides: Slide[]): void {
    console.log('[SlideIndex] Building index from', slides.length, 'slides');
    
    this.slides = [...slides];
    this.slideMap.clear();
    
    for (const slide of this.slides) {
      this.slideMap.set(slide.id, slide);
    }
    
    this.reindexSlides();
    
    console.log('[SlideIndex] ✅ Index built');
  }
  
  /**
   * Get statistics
   */
  getStats() {
    return {
      slideCount: this.slides.length,
      firstSlide: this.slides[0]?.id,
      lastSlide: this.slides[this.slides.length - 1]?.id
    };
  }
}

